import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlanContext = createContext(null);

export function PlanProvider({ children }) {
    const { user, getAuthHeader } = useAuth();
    const [planData, setPlanData] = useState(null);
    const [tiers, setTiers] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadPlan = useCallback(async () => {
        if (!user) { setLoading(false); return; }
        try {
            const [planRes, tiersRes] = await Promise.all([
                axios.get(`${API}/plans/current`, { headers: getAuthHeader() }),
                axios.get(`${API}/plans/tiers`),
            ]);
            setPlanData(planRes.data);
            setTiers(tiersRes.data);
        } catch {
            // Silent fail — plan data is non-critical
        } finally {
            setLoading(false);
        }
    }, [user, getAuthHeader]);

    useEffect(() => { loadPlan(); }, [loadPlan]);

    const checkFeature = useCallback((feature) => {
        if (!planData) return true; // Allow if plan data not loaded
        return planData.features?.[feature] !== false;
    }, [planData]);

    const checkLimit = useCallback((resource) => {
        if (!planData) return { allowed: true, current: 0, limit: -1, percentage: 0 };
        const usage = planData.usage?.[resource];
        if (!usage) return { allowed: true, current: 0, limit: -1, percentage: 0 };
        return {
            allowed: usage.unlimited || usage.current < usage.limit,
            current: usage.current,
            limit: usage.limit,
            unlimited: usage.unlimited,
            percentage: usage.percentage,
        };
    }, [planData]);

    const enabledModules = planData?.enabled_modules || [];

    const checkModule = useCallback((moduleKey) => {
        if (!planData) return false;
        return (planData.enabled_modules || []).includes(moduleKey);
    }, [planData]);

    const value = {
        plan: planData?.plan || 'free',
        planLabel: planData?.plan_label || 'Free',
        planData,
        tiers,
        loading,
        checkFeature,
        checkLimit,
        checkModule,
        enabledModules,
        refreshPlan: loadPlan,
    };

    return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
}

export function usePlan() {
    const ctx = useContext(PlanContext);
    if (!ctx) return { plan: 'free', planLabel: 'Free', checkFeature: () => true, checkLimit: () => ({ allowed: true }), checkModule: () => false, enabledModules: [], loading: true };
    return ctx;
}
